<script lang="ts">
interface Props {
    modelValue: boolean
    packagingOrder: PackagingOrder
    status: PackagingOrderStatus
}
</script>

<script setup lang="ts">
import type { MinimalResource, ResourceResponse, uuid } from '@/types/general'

import { watch, computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import Datepicker from '@vuepic/vue-datepicker'

import useForm from '@/hooks/use-form'
import { useAuthStore } from '@/stores/auth-store'
import { emitter } from '@/utils/mitt'
import { PackagingOrder, PackagingOrderStatus, PackagingOrderType } from '@/types/packaging'
import { packagingOrderStatus } from '@/utils/packaging-order'
import { DropdownStringOption } from '@/types/inputs'

import MySelect from '@/components/my-components/form/MySelect.vue'
import MyInputLabel from '@/components/my-components/form/MyInputLabel.vue'
import MyTextarea from '@/components/my-components/form/MyTextarea.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'

interface Form {
    supplierLocationId: uuid | null
    status: PackagingOrderStatus
    note: string
    plannedAt: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'close'): void
    (e: 'saved'): void
}>()

const authStore = useAuthStore()
const { t } = useI18n()
const { data, submit, errors, reset, loading } = useForm<Form>({
    supplierLocationId: props.packagingOrder.supplierLocation?.id ?? null,
    status: props.status,
    note: '',
    plannedAt: props.packagingOrder.plannedAt,
})

const supplierLocations = ref<DropdownStringOption[]>([])

const title = computed(() => {
    if (props.status === PackagingOrderStatus.Accepted) {
        return t('acceptPackagingOrder')
    } else if (props.status === PackagingOrderStatus.Canceled) {
        return t('cancelPackagingOrder')
    } else if (props.status === PackagingOrderStatus.Finished) {
        return t('finishPackagingOrder')
    }
    return t('changePackagingOrderStatus')
})

const buttonText = computed(() => {
    if (props.status === PackagingOrderStatus.Accepted) {
        return t('acceptPackagingOrder')
    } else if (props.status === PackagingOrderStatus.Canceled) {
        return t('cancelPackagingOrder')
    } else if (props.status === PackagingOrderStatus.Finished) {
        return t('finishPackagingOrder')
    }
    return t('changePackagingOrder')
})

const companyIsSupplier = computed(() => {
    return props.packagingOrder.supplierCompany.id === authStore.companyId
})

async function fetchLocations(): Promise<DropdownStringOption[]> {
    const response = await axios.get<MinimalResource[]>(
        window.route('minimal.companies.locations', {
            company: props.packagingOrder.supplierCompany.id,
        }),
    )

    return response.data.map((location) => ({ value: location.id, label: location.name }))
}

async function onSubmit() {
    const response = await submit<ResourceResponse<PackagingOrder>>(
        'put',
        window.route('packaging.company.packaging-orders.update-status', {
            company: authStore.companyId,
            packagingOrder: props.packagingOrder,
        }),
    )

    if (response !== undefined) {
        emit('close')
        emit('saved')
        emitter.emit('fetchPackagingOrders')
    }
}

watch(
    () => props.modelValue,
    () => {
        if (!props.modelValue) return
        reset({
            supplierLocationId: props.packagingOrder.supplierLocation?.id ?? null,
            status: props.status,
            note: '',
        })
    },
)

onMounted(async () => {
    if (data.status === PackagingOrderStatus.Accepted && companyIsSupplier.value) {
        supplierLocations.value = await fetchLocations()
    }
})
</script>

<template>
    <MyModal :value="props.modelValue" :max-width="400" @close="emit('close')">
        <LoaderWrapper :visible="loading" class="rounded-xl" />
        <template #title>
            {{ title }}
        </template>
        <MyForm :errors="errors" class="flex flex-col space-y-4" @submit.prevent="onSubmit">
            <span
                v-if="props.status === PackagingOrderStatus.Finished"
                class="text-xs text-stone-600 dark:text-primary-200"
                v-text="t('packagingOrderTransactionExplanation')"
            />
            <div v-if="props.status">
                <MyInputLabel v-text="t('status')" />
                <h4
                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                    v-text="t(packagingOrderStatus[props.status])"
                />
            </div>

            <MySelect
                v-if="data.status === PackagingOrderStatus.Accepted && companyIsSupplier"
                v-model="data.supplierLocationId"
                name="supplierLocationId"
                :options="supplierLocations"
                :label="t('supplierLocation')"
                searchable
            />

            <MyTextarea v-model="data.note" name="note" :label="t('comment')" />

            <div
                v-if="authStore.companyId === packagingOrder.supplierCompany.id"
                class="flex flex-col w-full space-y-2"
            >
                <MyInputLabel
                    v-text="
                        t(
                            props.packagingOrder.type === PackagingOrderType.Delivery
                                ? 'deliveryDate'
                                : 'pickupDate',
                        )
                    "
                />
                <Datepicker
                    v-model="data.plannedAt"
                    class="input-field rounded-xl border border-stone-300"
                    name="plannedAt"
                    close-on-scroll
                    :enable-time-picker="false"
                    auto-apply
                    auto-position
                    position="left"
                    format="yyyy-MM-dd"
                    :transitions="false"
                    :placeholder="t('selectDate')"
                />
            </div>
            <div class="flex justify-end">
                <MyButton
                    :disabled="loading"
                    :scheme="props.status == PackagingOrderStatus.Canceled ? 'danger' : 'primary'"
                    v-text="buttonText"
                />
            </div>
        </MyForm>
    </MyModal>
</template>
